<template>
  <div class="settings-popup-overlay">
    <div class="settings-popup">
      <img
        src="@/assets/walkman/ic-close.png"
        alt=""
        class="ic-close"
        @click="handleClose"
      />
      <div class="popup-header">
        <div class="popup-title">设置</div>
      </div>
      <div class="popup-content">
        <div class="settings-content">
          <!-- 开关选项 -->
          <div class="setting-item">
            <span class="setting-label">朗读单词释义</span>
            <div
              class="toggle-switch"
              :class="{ active: localSettings.wordTranslateStatus === 1 }"
              @click="
                localSettings.wordTranslateStatus = localSettings.wordTranslateStatus === 1 ? 0 : 1
              "
            >
              <div class="toggle-slider"></div>
            </div>
          </div>
          <!-- <div class="setting-item">
            <span class="setting-label">朗读音节拆分</span>
            <div
              class="toggle-switch"
              :class="{ active: localSettings.wordSyllablesStatus === 1 }"
              @click="localSettings.wordSyllablesStatus = localSettings.wordSyllablesStatus === 1 ? 0 : 1"
            >
              <div class="toggle-slider"></div>
            </div>
          </div> -->
          <div class="setting-item">
            <span class="setting-label">朗读例句</span>
            <div
              class="toggle-switch"
              :class="{ active: localSettings.wordExampleStatus === 1 }"
              @click="localSettings.wordExampleStatus = localSettings.wordExampleStatus === 1 ? 0 : 1"
            >
              <div class="toggle-slider"></div>
            </div>
          </div>
          <div class="setting-item">
            <span class="setting-label">循环播放</span>
            <div
              class="toggle-switch"
              :class="{ active: localSettings.loopStatus === 1 }"
              @click="localSettings.loopStatus = localSettings.loopStatus === 1 ? 0 : 1"
            >
              <div class="toggle-slider"></div>
            </div>
          </div>

          <!-- 播放次数选择 -->
          <div class="play-count-section">
            <div class="play-count-label">单词播放次数:</div>
            <div class="play-count-buttons">
              <div
                class="count-btn"
                :class="{ active: localSettings.loopCount === 1 }"
                @click="selectPlayCount(1)"
              >
                1次
              </div>
              <div
                class="count-btn"
                :class="{ active: localSettings.loopCount === 2 }"
                @click="selectPlayCount(2)"
              >
                2次
              </div>
              <div
                class="count-btn"
                :class="{ active: localSettings.loopCount === 3 }"
                @click="selectPlayCount(3)"
              >
                3次
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="confirm-btn" @click="handleConfirm">确 定</div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";

const props = defineProps({
  settings: {
    type: Object,
    default: () => ({
      wordTranslateStatus: 1, // 默认开启
      wordSyllablesStatus: 0, // 默认关闭
      wordExampleStatus: 1, // 默认开启
      loopStatus: 1, // 默认开启
      loopCount: 1, // 默认播放1次
    }),
  },
});

const emit = defineEmits(["close", "confirm"]);

// 辅助函数：确保开关默认值为指定值（如果值为 undefined、null，使用默认值）
const ensureDefault = (value, defaultValue = 1) => {
  return value === undefined || value === null ? defaultValue : value;
};

// 辅助函数：确保播放次数默认值为 1（如果值为 undefined、null 或无效值，都使用默认值 1）
const ensureDefaultCount = (value, defaultValue = 1) => {
  if (value === undefined || value === null || value === 0) return defaultValue;
  // 确保是有效的数字（1、2、3）
  return [1, 2, 3].includes(value) ? value : defaultValue;
};

// 初始化 localSettings，确保所有开关有正确的默认值
const localSettings = ref({
  wordTranslateStatus: ensureDefault(props.settings?.wordTranslateStatus, 1), // 默认开启
  wordSyllablesStatus: ensureDefault(props.settings?.wordSyllablesStatus, 0), // 默认关闭
  wordExampleStatus: ensureDefault(props.settings?.wordExampleStatus, 1), // 默认开启
  loopStatus: ensureDefault(props.settings?.loopStatus, 1), // 默认开启
  loopCount: ensureDefaultCount(props.settings?.loopCount), // 默认播放1次
});

// 监听设置变化，确保从 Android 加载的设置能正确回显
watch(
  () => props.settings,
  (newVal) => {
    if (newVal) {
      localSettings.value = {
        wordTranslateStatus: ensureDefault(newVal.wordTranslateStatus, 1), // 默认开启
        wordSyllablesStatus: ensureDefault(newVal.wordSyllablesStatus, 0), // 默认关闭
        wordExampleStatus: ensureDefault(newVal.wordExampleStatus, 1), // 默认开启
        loopStatus: ensureDefault(newVal.loopStatus, 1), // 默认开启
        loopCount: ensureDefaultCount(newVal.loopCount), // 默认播放1次
      };
      console.log("SettingsPopup: 设置已更新，localSettings:", localSettings.value);
    } else {
      // 如果 newVal 为空，使用全部默认值
      localSettings.value = {
        wordTranslateStatus: 1,
        wordSyllablesStatus: 0, // 默认关闭
        wordExampleStatus: 1,
        loopStatus: 1,
        loopCount: 1,
      };
    }
  },
  { deep: true, immediate: true }
);

const handleClose = () => {
  emit("close");
};

const selectPlayCount = (count) => {
  localSettings.value.loopCount = count;
};

const handleConfirm = () => {
  emit("confirm", { ...localSettings.value });
  emit("close");
};
</script>

<style scoped lang="scss">
.settings-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;

  * {
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }

  .settings-popup {
    position: relative;
    width: 379px;
    height: 437px;
    background: linear-gradient(
      180deg,
      #cbb8ff 0%,
      #eee8ff 6%,
      #ffffff 17%,
      #ffffff 100%
    );
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    padding: 13px 32px 29px;
    box-sizing: border-box;

    .popup-header {
      position: relative;
      box-sizing: border-box;
      margin-bottom: 24px;

      .popup-title {
        text-align: center;
        font-weight: bold;
        font-size: 16px;
        color: #333333;
      }
    }

    .popup-content {
      flex: 1;
      overflow-y: auto;
      box-sizing: border-box;
      padding: 0 10px;
    }
  }

  // 设置内容样式
  .settings-content {
    box-sizing: border-box;

    .setting-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
      &:last-of-type {
        margin-bottom: 0;
      }

      .setting-label {
        font-size: 15px;
        color: #333333;
        font-weight: 500;
      }

      .toggle-switch {
        position: relative;
        width: 53px;
        height: 28px;
        background: #e0e0e0;
        border-radius: 22px;
        cursor: pointer;
        transition: background-color 0.3s;
        flex-shrink: 0;
        margin-right: 12px;

        &.active {
          background: #a07dff;
        }

        .toggle-slider {
          position: absolute;
          top: 50%;
          left: 4px;
          width: 18.5px;
          height: 18px;
          background: #ffffff;
          border-radius: 50%;
          transition: transform 0.3s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          transform: translateY(-50%);
        }

        &.active .toggle-slider {
          transform: translateX(28.5px) translateY(-50%);
        }
      }
    }

    .play-count-section {
      .play-count-label {
        font-size: 15px;
        color: #333333;
        font-weight: 500;
        margin-bottom: 5px;
      }

      .play-count-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .count-btn {
          width: 90px;
          height: 44px;
          background: #f1ecff;
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e0e0e0;
          border-radius: 44px;
          background: white;
          font-size: 14px;
          color: #666666;
          cursor: pointer;
          transition: all 0.3s;

          &.active {
            border-color: #8456ff;
            background: #f5f0ff;
            color: #8456ff;
            font-weight: 600;
          }
        }
      }
    }
  }

  .confirm-btn {
    margin: 0 auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 315px;
    height: 45px;
    background: linear-gradient(90deg, #bba3fe 0%, #a07dff 100%);
    box-shadow: 0px 3px 6px 1px rgba(133, 103, 254, 0.2),
      inset 0px 3px 10px 1px rgba(255, 255, 255, 0.26);
    border-radius: 22px;
    font-weight: bold;
    font-size: 15px;
    color: #ffffff;
  }
}
.ic-close {
  position: absolute;
  width: 15px;
  height: 15px;
  top: 13px;
  right: 13px;
  cursor: pointer;
  pointer-events: auto;
}
</style>
